import { useState, useRef, useCallback, useEffect } from "react";
import { ZONE_CONFIGS, SCOPES, ZONE_IDS, findZoneByKeyCode } from "./keyboardShortcutRegistry";

const FOCUS_RING_HIDE_DELAY = 3000;

const useZoneNavigation = ({ layout, enabled, zoneRefs }) => {
  const [activeZoneId, setActiveZoneId] = useState(null);
  const [showFocusRing, setShowFocusRing] = useState(false);
  const [helpOverlayVisible, setHelpOverlayVisible] = useState(false);

  const suppressAutoFocusRef = useRef(false);
  const previousZoneRef = useRef(null);
  const focusRingTimerRef = useRef(null);
  const lastInteractionRef = useRef("mouse");

  const layoutScope =
    layout === "supermarket" ? SCOPES.SUPERMARKET : SCOPES.RESTAURANT;
  const zones = ZONE_CONFIGS[layoutScope] || [];

  const isModalOpen = useCallback(() => {
    return (
      !!document.querySelector(".ant-modal-mask") ||
      !!document.querySelector(".ant-drawer-open") ||
      document.body.classList.contains("ant-modal-open")
    );
  }, []);

  const clearFocusRingTimer = useCallback(() => {
    if (focusRingTimerRef.current) {
      clearTimeout(focusRingTimerRef.current);
      focusRingTimerRef.current = null;
    }
  }, []);

  const showFocusRingTemporarily = useCallback(() => {
    setShowFocusRing(true);
    clearFocusRingTimer();
    focusRingTimerRef.current = setTimeout(() => {
      setShowFocusRing(false);
    }, FOCUS_RING_HIDE_DELAY);
  }, [clearFocusRingTimer]);

  const focusZone = useCallback(
    (zoneId) => {
      if (isModalOpen()) return;

      const zone = zones.find((z) => z.id === zoneId);
      if (!zone) return;

      previousZoneRef.current = activeZoneId;
      setActiveZoneId(zoneId);
      lastInteractionRef.current = "keyboard";

      const isSearchZone =
        zoneId === ZONE_IDS.SEARCH || zoneId === ZONE_IDS.SIDEBAR;
      suppressAutoFocusRef.current = !isSearchZone;

      showFocusRingTemporarily();

      const el =
        zoneRefs?.[zoneId]?.current ||
        document.querySelector(`[data-zone="${zoneId}"]`);
      if (el) {
        const focusTarget =
          el.querySelector("[data-zone-focus-target]") || el;
        focusTarget.focus({ preventScroll: false });
      }
    },
    [activeZoneId, zones, zoneRefs, isModalOpen, showFocusRingTemporarily]
  );

  const handleFKeyPress = useCallback(
    (keyCode, event) => {
      if (!enabled) return false;

      if (keyCode === 112) {
        event.preventDefault();
        setHelpOverlayVisible((v) => !v);
        return true;
      }

      if (isModalOpen()) return false;

      const zone = findZoneByKeyCode(keyCode, layoutScope);
      if (!zone) return false;

      event.preventDefault();
      focusZone(zone.id);
      return true;
    },
    [enabled, layoutScope, focusZone, isModalOpen]
  );

  const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  const handleInnerNavigation = useCallback(
    (event) => {
      if (!enabled || !activeZoneId) return false;
      if (isModalOpen()) return false;

      const { keyCode } = event;
      const isArrow = keyCode >= 37 && keyCode <= 40;
      const isTab = keyCode === 9;
      if (!isArrow && !isTab) return false;

      const zoneEl = document.querySelector(`[data-zone="${activeZoneId}"]`);
      if (!zoneEl) return false;

      const focusables = Array.from(zoneEl.querySelectorAll(FOCUSABLE_SELECTOR))
        .filter((el) => !el.disabled && el.offsetParent !== null);
      if (focusables.length === 0) return false;

      const currentIndex = focusables.indexOf(document.activeElement);
      if (currentIndex === -1) return false;

      let nextIndex = currentIndex;
      const isForward = keyCode === 40 || keyCode === 39 || (isTab && !event.shiftKey);
      const isBackward = keyCode === 38 || keyCode === 37 || (isTab && event.shiftKey);

      if (isForward) {
        nextIndex = (currentIndex + 1) % focusables.length;
      } else if (isBackward) {
        nextIndex = (currentIndex - 1 + focusables.length) % focusables.length;
      }

      if (nextIndex !== currentIndex) {
        event.preventDefault();
        focusables[nextIndex].focus({ preventScroll: false });
        showFocusRingTemporarily();
        return true;
      }
      return false;
    },
    [enabled, activeZoneId, isModalOpen, showFocusRingTemporarily]
  );

  const clearZoneFocus = useCallback(() => {
    setActiveZoneId(null);
    setShowFocusRing(false);
    suppressAutoFocusRef.current = false;
    clearFocusRingTimer();
  }, [clearFocusRingTimer]);

  const restorePreviousZone = useCallback(() => {
    if (previousZoneRef.current) {
      focusZone(previousZoneRef.current);
    } else {
      clearZoneFocus();
    }
  }, [focusZone, clearZoneFocus]);

  useEffect(() => {
    document.querySelectorAll("[data-zone].keyboard-zone-active").forEach((el) => {
      el.classList.remove("keyboard-zone-active");
    });
    if (activeZoneId && showFocusRing) {
      const el = document.querySelector(`[data-zone="${activeZoneId}"]`);
      if (el) el.classList.add("keyboard-zone-active");
    }
  }, [activeZoneId, showFocusRing]);

  useEffect(() => {
    const handleMouseDown = () => {
      lastInteractionRef.current = "mouse";
      clearZoneFocus();
    };
    window.addEventListener("mousedown", handleMouseDown);
    return () => window.removeEventListener("mousedown", handleMouseDown);
  }, [clearZoneFocus]);

  useEffect(() => {
    return () => clearFocusRingTimer();
  }, [clearFocusRingTimer]);

  return {
    activeZoneId,
    showFocusRing,
    helpOverlayVisible,
    setHelpOverlayVisible,
    suppressAutoFocusRef,
    lastInteractionRef,
    zones,
    handleFKeyPress,
    handleInnerNavigation,
    focusZone,
    clearZoneFocus,
    restorePreviousZone,
    isModalOpen,
  };
};

export default useZoneNavigation;
